class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async readSections() {
    const ids = this.getSectionsToRender().map(section => section.id);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`${routes.cart_url}?sections=${ids.join(',')}`, {credentials:'same-origin', headers:{Accept:'application/json'}, signal:controller.signal});
      if (!response.ok) throw new Error(window.cartStrings.error);
      const sections = await response.json();
      if (ids.some(id => typeof sections[id] !== 'string' || !sections[id])) throw new Error(window.cartStrings.error);
      return sections;
    } finally { clearTimeout(timeout); }
  }

  async refreshContents() {
    const sections = await this.readSections();
    const document = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const source = document.querySelector('cart-drawer');
    if (!source) throw new Error(window.cartStrings.error);
    this.classList.toggle('is-empty', source.classList.contains('is-empty'));
    this.renderContents({sections});
    const focus = this.querySelector('.drawer__close');
    if (focus) trapFocus(this.querySelector('.drawer__inner'), focus);
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    // Reopening must not retain an old error or a spinner from a detached cart row.
    if (this.dataset.cartUpdating !== 'true') {
      const stale = [...this.querySelectorAll('#CartDrawer-CartErrors, .cart-item__error-text')].some(node => node.textContent.trim())
        || this.querySelector('.loading__spinner:not(.hidden)');
      this.querySelectorAll('#CartDrawer-CartErrors, .cart-item__error-text').forEach(node => { node.textContent = ''; });
      this.querySelectorAll('.loading__spinner').forEach(node => node.classList.add('hidden'));
      this.querySelectorAll('.cart__items--disabled').forEach(node => node.classList.remove('cart__items--disabled'));
      if (stale) this.refreshContents().catch(console.warn);
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Focus must also work when reduced-motion disables the transition.
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : this.querySelector('#CartDrawer');
      trapFocus(container, this.querySelector('.drawer__close'));
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  async updateQuantity(line, quantity, event, name) {
    const drawer = this.closest('cart-drawer');
    if (!drawer || drawer.dataset.cartUpdating === 'true') return;
    const input = this.querySelector(`#Drawer-quantity-${line}`);
    const key = input?.dataset.quantityLineKey;
    const restoreName = name || document.activeElement?.getAttribute('name');
    const controls = [...drawer.querySelectorAll('.quantity button, .quantity input, cart-remove-button button, #CartDrawer-Checkout')].map(node => [node, node.disabled]);
    const sectionIds = drawer.getSectionsToRender().map(section => section.id);
    let accepted = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    drawer.dataset.cartUpdating = 'true';
    drawer.setAttribute('aria-busy', 'true');
    controls.forEach(([node]) => { node.disabled = true; });
    drawer.querySelectorAll('#CartDrawer-CartErrors, .cart-item__error-text').forEach(node => { node.textContent = ''; });
    this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`).forEach(node => node.classList.remove('hidden'));
    try {
      const response = await fetch(`${routes.cart_change_url}.js`, {
        ...fetchConfig(), signal: controller.signal,
        body: JSON.stringify({...(key ? {id: key} : {line}), quantity,
          sections: sectionIds, sections_url: window.location.pathname})
      });
      const state = await response.json();
      if (!response.ok || state.errors || state.status) {
        throw new Error(typeof state.errors === 'string' ? state.errors : state.description || window.cartStrings.error);
      }
      accepted = true;
      // Shopify may accept the change but return null section HTML. Retry the read only.
      if (sectionIds.some(id => !state.sections?.[id])) state.sections = await drawer.readSections();
      drawer.classList.toggle('is-empty', state.item_count === 0);
      drawer.renderContents(state);
      const nextInput = drawer.querySelector(`#Drawer-quantity-${line}`);
      const focus = state.item_count === 0
        ? drawer.querySelector('[data-moviwi-cart-continue]') || drawer.querySelector('.drawer__close')
        : (restoreName === 'updates[]' ? nextInput : drawer.querySelector('.drawer__close'));
      if (focus) trapFocus(drawer.querySelector('.drawer__inner'), focus);
      // Subscriber/analytics failures must not turn a successful cart update into a cart error.
      Promise.resolve().then(() => publish(PUB_SUB_EVENTS.cartUpdate, {source:'cart-items', cartData:state})).catch(console.warn);
    } catch (error) {
      console.warn('Cart quantity update:', error);
      // A failed response can still follow an accepted server change; reconcile without replaying it.
      let recovered = false;
      try {
        await drawer.refreshContents();
        const current = key ? drawer.querySelector(`[data-quantity-line-key="${key}"]`) : drawer.querySelector(`#Drawer-quantity-${line}`);
        recovered = quantity === 0 ? !current : Number(current?.value) === Number(quantity);
      } catch (refreshError) { console.warn('Cart refresh:', refreshError); }
      if (!recovered) {
        const message = drawer.querySelector('#CartDrawer-CartErrors');
        if (message) message.textContent = accepted ? window.cartStrings.error : error.message || window.cartStrings.error;
      }
    } finally {
      clearTimeout(timeout);
      drawer.dataset.cartUpdating = 'false';
      drawer.removeAttribute('aria-busy');
      controls.forEach(([node, disabled]) => { if (node.isConnected) node.disabled = disabled; });
      drawer.querySelectorAll('.loading__spinner').forEach(node => node.classList.add('hidden'));
      drawer.querySelectorAll('.cart__items--disabled').forEach(node => node.classList.remove('cart__items--disabled'));
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
